import { CommonModule } from '@angular/common';
import { afterRenderEffect, Component, computed, effect, ElementRef, inject, OnDestroy, QueryList, signal, ViewChild, ViewChildren, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChatService } from '../../../services/chat/chat.service';
import { VoiceService } from '../../../services/voice/voice.service';
import { PlayerList } from "./components/player-list/player-list";
import { RoomList } from "./components/room-list/room-list";
import { Message } from "../../../components/chat/message/message";
import { CreateRoomModal } from "../../../components/modals/create-room-modal/create-room-modal";
import { EditRoomModal } from "../../../components/modals/edit-room-modal/edit-room-modal";
import { ExternalLinkModal } from "../../../components/modals/external-link-modal/external-link-modal";
import { ClearChatModal } from "../../../components/modals/clear-chat-modal/clear-chat-modal";
import { IdentityService } from '../../../services/identity/identity.service';
import { GiphyService } from '../../../services/giphy/giphy.service';
import { MessengerService } from '../../../services/messenger/messenger.service';
import { EmojiPicker } from '../../../components/chat/emoji-picker/emoji-picker';
import { UserProfileModal } from '../../../components/modals/user-profile-modal/user-profile-modal';
import { ServerBrowserModal } from '../../../components/modals/server-browser-modal/server-browser-modal';
import { GameSettingsModal, GameSettingsData } from '../../../components/modals/game-settings-modal/game-settings-modal';
import { GamesService } from '../../../services/games/games.service';
import { UpdateGameModal, PromptGameInfo } from '../../../components/modals/update-game-modal/update-game-modal';
import { HasRoleDirective } from '../../../directives/has-role.directive';

export interface User {
  name: string;
  avatar: string;
}

@Component({
  selector: 'app-chat',
  imports: [CommonModule, FormsModule, PlayerList, RoomList, Message, CreateRoomModal, EditRoomModal, ExternalLinkModal, ClearChatModal, EmojiPicker, UserProfileModal, ServerBrowserModal, GameSettingsModal, UpdateGameModal, HasRoleDirective],
  templateUrl: './chat.html',
  styleUrl: './chat.scss',
})
export class Chat implements OnDestroy {
  private voiceSubscription?: Subscription;
  private chatService = inject(ChatService);
  private router = inject(Router);
  private voiceService = inject(VoiceService);
  private identityService = inject(IdentityService);
  private messengerService = inject(MessengerService);
  private gamesService = inject(GamesService);
  public giphy = inject(GiphyService);

  @ViewChild('msgEl')
  private chatMessagesEl!: ElementRef<HTMLDivElement>;
  @ViewChild('bottomAnchor')
  private bottomAnchor!: ElementRef<HTMLDivElement>;

  localStream: WritableSignal<MediaStream | null> = signal(null);
  remoteStreams: WritableSignal<Map<string, MediaStream>> = signal(new Map());
  @ViewChildren('remoteVideo')
  remoteVideos!: QueryList<ElementRef<HTMLVideoElement>>;


  //webrtcConnected = this.voiceService;

  isConnected = this.chatService.isConnected;
  arena = this.chatService.arena;
  users = this.chatService.users;
  members = this.chatService.members;
  messages = this.chatService.messages;
  games = this.chatService.games;
  selectedServer = signal<any>(null);
  isElectron = signal<boolean>(this.checkElectron());

  @ViewChild('gameSettingsModalRef')
  gameSettingsModal!: GameSettingsModal;
  gameSettingsData = signal<GameSettingsData | null>(null);

  isMuted = signal(true);
  isDeafened = signal(false);

  // --- State Signals ---
  newMessage = signal('');
  zoomLevel = signal<number>(0);
  showGiphy = signal(false);
  showEmoji = signal(false);
  showCelebration = signal(false);
  showBirthday = signal(false);
  showSnow = signal(false);
  showFireworks = signal(false);
  private celebrationTimeout: any;
  private birthdayTimeout: any;
  private snowTimeout: any;
  private fireworksTimeout: any;
  private lastProcessedMessageGuid = '';
  private lastArenaGuid = '';
  private hasScrolledToBottomOnLoad = false;
  private wasNearBottom = false;
  private hasSetLoadListener = false;

  // --- Computed Signals ---  

  isReady = computed(() => {
    const claims = this.identityService.getClaims();
    const currentUserName = claims?.unique_name;
    if (!currentUserName) return false;

    const me = this.users().find(u => u.userName === currentUserName);
    return me ? me.status === 5 : false;
  });

  canEditLobby = computed(() => {
    const a = this.arena();
    if (!a) return false;


    const claims = this.identityService.getClaims();
    if (!claims) return false;

    console.log("My claims => ", claims);

    // Check system admin role
    const isSystemAdmin = claims.role === 'admin';
    if (isSystemAdmin) return true;

    // Standard user ID checks
    const userId = claims.nameid;
    const isOwner = a.ownerId === userId;
    if (isOwner) return true;

    // Check membership role in arena users
    const myUser = this.members().find(u => u.userName.toLowerCase() === claims.unique_name.toLowerCase());
    if (myUser && myUser.role === 'admin') return true;

    return false;
  });

  constructor(
    private route: ActivatedRoute
  ) {
    let guid = this.route.snapshot.paramMap.get('id');
    if (guid)
      this.chatService.connect(guid);

    effect(async () => {
      if (this.arena()) {
        // Connect
        if (this.arena() && this.arena().hasVoice) {
          this.voiceService.connect(this.arena().guid);
        }
        /*await this.webrtcService.connect('http://localhost:3000');

        

        // Join room
        await this.webrtcService.joinRoom('test');

        // Produce audio and video
        await this.webrtcService.produce('audio');
        await this.webrtcService.produce('video');

        // Bind Webrtc service signals to component signals
        this.webrtcService.localStream$.subscribe(stream => this.localStream.set(stream));
        this.webrtcService.remoteStreams$.subscribe(map => this.remoteStreams.set(new Map(map)));*/
      }

      const streams = this.remoteStreams();

      queueMicrotask(() => {
        const videoElements = this.remoteVideos?.toArray() ?? [];

        streams.forEach((stream, peerId) => {
          const videoEl = videoElements.find(v => !v.nativeElement.srcObject);

          if (videoEl && videoEl.nativeElement.srcObject !== stream) {
            console.log("Setting element stream and playing");
            videoEl.nativeElement.srcObject = stream;
            videoEl.nativeElement.play().catch(console.error);
          }
        })
      });
    });

    // Is talking check.
    this.voiceSubscription = this.voiceService.eventChannel.subscribe((channel) => {
      if (this.users) {
        this.users().forEach((user) => {
          let peer = channel.peers.find((p: any) =>
            p.userNameSlug && user.userNameSlug &&
            p.userNameSlug.toLowerCase() === user.userNameSlug.toLowerCase()
          );
          user.isTalking = peer ? peer.isTalking : false;
        });
        this.chatService.users.set([...this.users()]);
      }
    });

    effect(() => {
      const msgs = this.messages();
      if (msgs && msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1];
        const identifier = lastMsg.guid || lastMsg.timestamp?.toString() || lastMsg.text;

        if (identifier && identifier !== this.lastProcessedMessageGuid) {
          const isInitial = !this.lastProcessedMessageGuid;
          this.lastProcessedMessageGuid = identifier;

          if (!isInitial && lastMsg.text) {
            const text = lastMsg.text.toLowerCase();
            const celebrationWords = ['congrats', 'congratulations', 'grats', 'gratz', 'hurray', 'hell yeah'];
            const snowWords = ['merry christmas', 'snowing'];

            if (celebrationWords.some(word => text.includes(word))) {
              this.triggerCelebration();
            }
            if (snowWords.some(word => text.includes(word))) {
              this.triggerSnow();
            }
            if (text.includes('happy birthday')) {
              this.triggerBirthday();
            }
            if (text.includes('boom')) {
              this.triggerFireworks();
            }
          }
        }
      }
    });

    /*
     * Prompt Electron user to update game installation folder when entering a lobby with game type 2.
     */
    effect(async () => {
      const currentArena = this.arena();
      const currentGames = this.games();

      if (currentArena && currentArena.guid && currentArena.guid !== this.checkedInstallationArenaGuid) {
        this.checkedInstallationArenaGuid = currentArena.guid;

        if (this.isElectron()) {
          const firstGame = currentGames && currentGames.length > 0 ? currentGames[0] : null;
          if (firstGame && firstGame.gameType === 2) {
            const rawId = firstGame.gameId || firstGame.id;
            const gameIdStr = String(rawId);
            const targetId = gameIdStr === '5' ? 'quake-2' : (gameIdStr === '6' ? 'quake-1' : gameIdStr);
            const title = firstGame.title || firstGame.name || 'Quake II';

            let installed = await this.gamesService.isGameInstalled(targetId);
            if (!installed && targetId !== gameIdStr) {
              installed = await this.gamesService.isGameInstalled(gameIdStr);
            }

            if (!installed) {
              this.uninstalledGame.set({ id: targetId, title });
              queueMicrotask(() => {
                const modalEl = document.getElementById('updateGameModal');
                if (modalEl) {
                  const modal = new (window as any).bootstrap.Modal(modalEl);
                  modal.show();
                }
              });
            }
          }
        }
      }
    });
  }

  uninstalledGame = signal<PromptGameInfo | null>(null);
  private checkedInstallationArenaGuid = '';

  /*
   * Called when game installation folder is updated via modal.
   * Re-verifies installation status and clears uninstalledGame signal.
   */
  public async onGameInstalled(): Promise<void> {
    this.uninstalledGame.set(null);
    await this.chatService.checkGameInstallation();
  }

  ngOnDestroy() {
    this.voiceSubscription?.unsubscribe();
  }


  /**
   * Handle scrolling in message container.
   */
  private readonly scrollEffect = afterRenderEffect(() => {
    const currentArena = this.arena();
    const currentMessages = this.messages();

    if (!this.chatMessagesEl) return;

    const el = this.chatMessagesEl.nativeElement;
    const anchor = this.bottomAnchor?.nativeElement;
    if (!el || !anchor) return;

    // Attach capturing load listener once to capture image load events
    if (!this.hasSetLoadListener) {
      this.hasSetLoadListener = true;
      el.addEventListener('load', (event) => {
        if (event.target instanceof HTMLImageElement) {
          const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
          if (this.wasNearBottom && dist <= 400) {
            anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
          }
        }
      }, true);
    }

    // Reset initial scroll flag if the arena changes
    const currentArenaGuid = currentArena?.guid || '';
    if (currentArenaGuid !== this.lastArenaGuid) {
      this.lastArenaGuid = currentArenaGuid;
      this.hasScrolledToBottomOnLoad = false;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (!this.hasScrolledToBottomOnLoad && currentMessages.length > 0) {
      anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
      this.hasScrolledToBottomOnLoad = true;
      this.wasNearBottom = true;
    } else if (distanceFromBottom <= 100) {
      anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
      this.wasNearBottom = true;
    } else {
      this.wasNearBottom = false;
    }
  })



  zoomIn() {
    this.zoomLevel.update(level => Math.min(3, level + 1));
  }

  zoomOut() {
    this.zoomLevel.update(level => Math.max(-3, level - 1));
  }

  /**
   * Leave the arena.
   */
  leave() {
    if (this.arena().parentGuid == null) {
      this.chatService.disconnect();
      this.router.navigate(['/home/overview']);
    } else {
      this.voiceService.disconnect();
      this.chatService.leave();
    }
  }

  /**
   * Send message to arena.
   * @param text 
   */
  send(text: string) {
    this.chatService.send(text);
    this.newMessage.set('');
  }

  /**
   * Search for GIFs via GIPHY service.
   * @param query 
   */
  giphySearch(query: string) {
    if (query.length < 2) return;
    this.giphy.search(query);
  }

  /**
   * Select a GIF and send it as a message.
   * @param gif 
   */
  selectGif(gif: any) {
    const attachment = {
      title: gif.title || 'Giphy',
      url: gif.images.original.url
    };
    this.chatService.send('', attachment);
    this.showGiphy.set(false);
  }

  toggleEmoji() {
    this.showEmoji.update(s => !s);
    if (this.showEmoji()) {
      this.showGiphy.set(false);
    }
  }

  toggleGiphy() {
    this.showGiphy.update(s => !s);
    if (this.showGiphy()) {
      this.showEmoji.set(false);
    }
  }

  insertEmoji(emoji: string, inputEl: HTMLInputElement) {
    const startPos = inputEl.selectionStart ?? inputEl.value.length;
    const endPos = inputEl.selectionEnd ?? inputEl.value.length;
    const originalValue = this.newMessage();

    const newValue = originalValue.substring(0, startPos) + emoji + originalValue.substring(endPos);
    this.newMessage.set(newValue);

    setTimeout(() => {
      inputEl.focus();
      const newCursorPos = startPos + emoji.length;
      inputEl.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }

  public openLink(url: string) {
    this.messengerService.externalUrl.set(url);
  }

  async start() {
    await this.voiceService.connect('http://localhost:3000');

  }


  // --- Methods ---
  toggleReady() {
    const claims = this.identityService.getClaims();
    const currentUserName = claims?.unique_name;
    if (!currentUserName) return;

    const me = this.users().find(u => u.userName === currentUserName);
    if (!me) return;

    const newStatus = me.status === 5 ? 1 : 5;
    this.chatService.setStatus(newStatus);
  }

  startGame() {

  }

  toggleMute() {
    if (this.isDeafened()) {
      return; // Cannot unmute while deafened
    }

    if (this.isMuted())
      this.voiceService.startTalking();
    else
      this.voiceService.stopTalking();

    this.isMuted.update(m => !m);
  }

  toggleDeafen() {
    this.isDeafened.update(d => !d);
    if (this.isDeafened()) {
      this.isMuted.set(true);
    }
  }

  private checkElectron(): boolean {
    return navigator.userAgent.toLowerCase().includes('electron');
  }

  public onServerSelected(server: any) {
    this.selectedServer.set(server);
  }

  launchGame() {
    let metaData = this.arena()?.url || '';
    const selected = this.selectedServer();
    const claims = this.identityService.getClaims();
    const moderatorUserId = claims?.sub || claims?.nameid || this.arena()?.ownerId || '';
    const gameSettings = this.gameSettingsModal?.getSettings() ?? this.gameSettingsData() ?? {};
    const roomGuid = this.arena()?.guid || '';

    if (this.games()[0]?.gameType === 2) {
      const serverInfo = selected ? {
        ipAddress: selected.ip,
        port: selected.port,
        password: selected.password || '',
      } : {};

      // Merge game settings from the modal (if available) with server info and roomGuid.
      metaData = JSON.stringify({
        roomGuid,
        ...serverInfo,
        ...gameSettings,
      });
    } else {
      metaData = JSON.stringify({
        roomGuid,
        expectedHostUserId: moderatorUserId,
        isQuickPlay: this.arena()?.isStatic || gameSettings.isQuickPlay || false,
        winScore: gameSettings.winScore ?? 250,
        loseScore: gameSettings.loseScore ?? -200,
        timer: gameSettings.timer ?? 30,
        mode: gameSettings.mode ?? 'Team',
        ...gameSettings,
      });
    }

    console.log("Moderator requested launch game with metadata:", metaData);
    this.chatService.launch(metaData);
  }

  goToSettings() {
    this.router.navigate(['/settings']);
  }

  triggerCelebration() {
    if (this.celebrationTimeout) {
      clearTimeout(this.celebrationTimeout);
    }
    this.showCelebration.set(false);
    setTimeout(() => {
      this.showCelebration.set(true);
      this.celebrationTimeout = setTimeout(() => {
        this.showCelebration.set(false);
      }, 5000);
    }, 10);
  }

  triggerBirthday() {
    if (this.birthdayTimeout) {
      clearTimeout(this.birthdayTimeout);
    }
    this.showBirthday.set(false);
    setTimeout(() => {
      this.showBirthday.set(true);
      this.birthdayTimeout = setTimeout(() => {
        this.showBirthday.set(false);
      }, 5000);
    }, 10);
  }

  triggerSnow() {
    if (this.snowTimeout) {
      clearTimeout(this.snowTimeout);
    }
    this.showSnow.set(false);
    setTimeout(() => {
      this.showSnow.set(true);
      this.snowTimeout = setTimeout(() => {
        this.showSnow.set(false);
      }, 5000);
    }, 10);
  }

  triggerFireworks() {
    if (this.fireworksTimeout) {
      clearTimeout(this.fireworksTimeout);
    }
    this.showFireworks.set(false);
    setTimeout(() => {
      this.showFireworks.set(true);
      this.fireworksTimeout = setTimeout(() => {
        this.showFireworks.set(false);
      }, 6000);
    }, 10);
  }
}
